import * as React from 'react';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';
import FormErrorMessage from '../../../components/ErrorMessage';
import DateTimeInput from '../../../components/DateTimeInput';

// TYPES
import { closeModalType } from '../../../components/GlobalModal';
import { IncidentFormTypes } from '../IncidentSubmit';

interface AddMedicalPersonnelModalTypes {
	closeModal?: closeModalType;
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
}

const AddMedicalPersonnelModal: React.FC<AddMedicalPersonnelModalTypes> = ({
	closeModal,
	incidentFormData,
	setIncidentFormData
}) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const initialValues = {
		name: '',
		role: '',
		phoneOrEmail: '',
		dateAndTime: ''
	};

	const validateEmail = (email: string | undefined) => {
		return Yup.string().email().isValidSync(email);
	};

	const phoneRegExp =
		/^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

	const validatePhone = (phone: number | undefined) => {
		return Yup.number()
			.integer()
			.positive()
			.test((phone) => {
				return phone && phoneRegExp.test(phone.toString()) ? true : false;
			})
			.isValidSync(phone);
	};

	const validationSchema = Yup.object({
		name: Yup.string().required('Required'),
		role: Yup.string().required('Required'),
		phoneOrEmail: Yup.string()
			.required('Phone or email is required')
			.test('phoneOrEmail', 'Phone or email is invalid', (value) => {
				return validateEmail(value) || validatePhone(parseInt(value ?? '0'));
			}),
		dateAndTime: Yup.string().required('Required')
	});

	const submitHandler = (values: any) => {
		const formData = {
			name: values.name as string,
			role: values.role as string,
			phoneOrEmail: values.phoneOrEmail as string,
			dateAndTime: values.dateAndTime as string
		};

		const currentMedicalPersonnel = incidentFormData.medicalPersonnel || [];

		const newMedicalPersonnel = [...currentMedicalPersonnel, formData];

		setIncidentFormData((prevState) => ({
			...prevState,

			medicalPersonnel: newMedicalPersonnel
		}));

		if (closeModal) {
			closeModal();
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={submitHandler}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form mtop-0">
						<>
							{showError ? (
								<FormErrorMessage message={formErrorMessage} />
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<div className="form-group">
													<label htmlFor="name" className="label">
														Full Name
													</label>

													<div className="form-field">
														<Field name="name" id="name" type="text" className="form-input" />

														<ErrorMessage name="name" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="role" className="label">
														Role
													</label>

													<div className="form-field">
														<Field
															name="role"
															id="role"
															type="text"
															className="form-input"
															placeholder={'E.g. paramedic, nurse, general practitioner...'}
														/>

														<ErrorMessage name="role" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="phoneOrEmail" className="label">
														Phone or email address
													</label>

													<div className="form-field">
														<Field name="phoneOrEmail" id="phoneOrEmail" type="text" className="form-input" />

														<ErrorMessage name="phoneOrEmail" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label className="label">Date and time of contact</label>

													<DateTimeInput
														dateTimeString={formik.values.dateAndTime}
														onChange={(val: string) => formik.setFieldValue('dateAndTime', val)}
														excludeFuture
													/>
												</div>

												<Button className="btn-primary full-width" type="submit">
													Add to the list
												</Button>
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</>
					</Form>
				);
			}}
		</Formik>
	);
};

export default AddMedicalPersonnelModal;
